import {ExpandMore, Gesture, PictureAsPdf} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React, {useMemo} from 'react';
import {FormProvider, useForm} from 'react-hook-form';
import {useSelector} from 'react-redux';
import {i18n} from 'src/i18n';
import authSelectors from 'src/modules/auth/authSelectors';
import {CHILD_TASKS_WIDE_TABLE_LAYOUT} from 'src/modules/tarea/childTasksWideTableLayout';
import TableHeaderLabel from 'src/modules/tarea/tableHeaderLabel';
import {
  buildParcialGroups,
  buildSubsectionHeaderSpans,
  getChildTableRowLabel,
  getExecutorSignatureColumnLabel,
  getSignaturePersonDisplayName,
  getParcialAnswerText,
  getParcialGroupTitle,
  getRecordId,
  hasSubsectionHeaderSpans,
  isChildTasksWideTableMode,
  isParcialAnswerAlert,
  PARCIAL_ANSWER_ALERT_CELL_SX,
} from 'src/modules/tarea/tareaParcialHelpers';
import Message from 'src/view/shared/message';
import Spinner from 'src/view/shared/Spinner';
import UserAutocompleteFormItem from 'src/view/user/autocomplete/UserAutocompleteFormItem';

export type TaskSection = {
  key: string;
  title: string;
  subtitle?: string | null;
  parcial?: any;
  tasks: any[];
};

export const getSignatureSource = (signature: any) =>
  signature?.firma || signature?.firmaRef?.downloadUrl || signature?.firmaRef?.publicUrl || '';

export const getExecutorSignature = (task: any) => {
  const executorId = getRecordId(task?.ejecutor);
  const signed = (task?.firmas || []).filter((signature) => signature?.firmada && getSignatureSource(signature));
  const executorSignature = signed.find((signature) => executorId && getRecordId(signature?.responsable) === executorId);
  return executorSignature || null;
};

const isPendingParcial = (parcial: any) => parcial?.estado === 'Pendiente' && parcial?.firmada !== true;

const stopAccordionToggle = (event: React.MouseEvent) => {
  event.stopPropagation();
};

export const buildTaskSections = (
  tasks: any[],
  sortedParciales: any[],
  partialVerificationEnabled: boolean,
): TaskSection[] => {
  if (!partialVerificationEnabled) {
    return [{key: 'all', title: i18n('entities.tarea.childTasksListTitle'), tasks}];
  }

  const allParcialChildIds = new Set<string>();
  sortedParciales.forEach((parcial) => {
    (parcial?.tareasVinculadas || []).forEach((task) => {
      const id = getRecordId(task);
      if (id) allParcialChildIds.add(id);
    });
  });

  const toParcialSection = (parcial: any): TaskSection => {
    const taskIds = new Set((parcial?.tareasVinculadas || []).map(getRecordId).filter(Boolean));
    return {
      key: getRecordId(parcial),
      parcial,
      title: getParcialGroupTitle(parcial),
      subtitle: parcial?.responsable
        ? i18n('entities.firma.parcial.validatorLabel', getSignaturePersonDisplayName(parcial.responsable))
        : null,
      tasks: tasks.filter((child) => taskIds.has(getRecordId(child))),
    };
  };

  const pending = sortedParciales.filter(isPendingParcial).map(toParcialSection);
  const completed = sortedParciales.filter((parcial) => !isPendingParcial(parcial)).map(toParcialSection);
  const unverifiedTasks = tasks.filter((child) => !allParcialChildIds.has(getRecordId(child)));
  const unverified = unverifiedTasks.length
    ? [
        {
          key: 'unverified',
          title: i18n('entities.firma.parcial.unverifiedGroup'),
          tasks: unverifiedTasks,
        },
      ]
    : [];

  return [...pending, ...unverified, ...completed].filter((section) => section.tasks.length > 0);
};

export function ExecutorSignatureCell({task}: {task: any}) {
  const signature = getExecutorSignature(task);
  const source = getSignatureSource(signature);
  const displayName =
    getSignaturePersonDisplayName(signature?.responsable) || getSignaturePersonDisplayName(task?.ejecutor);

  if (!source) {
    return (
      <Typography variant="body2" color="textSecondary">
        -
      </Typography>
    );
  }

  return (
    <Box>
      <Box sx={{borderBottom: '1px solid', borderColor: 'divider', width: 'fit-content', minWidth: 80, mb: 0.5}}>
        <img alt="signature" src={source} style={{maxHeight: 44, maxWidth: 120, display: 'block'}} />
      </Box>
      <Typography variant="caption" color="textSecondary">
        {displayName}
      </Typography>
    </Box>
  );
}

export function ParcialSectionHeader({
  section,
  canSignParcial,
  onSign,
  onExportPdf,
  exportingPdf,
}: {
  section: TaskSection;
  canSignParcial: (parcial: any) => boolean;
  onSign: (parcial: any) => void;
  onExportPdf?: () => void;
  exportingPdf?: boolean;
}) {
  return (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      <Typography variant="h6">{section.title}</Typography>
      <Chip size="small" label={section.tasks.length} />
      {section.subtitle && <Chip size="small" variant="outlined" label={section.subtitle} />}
      {section.parcial && canSignParcial(section.parcial) && (
        <Button
          variant="contained"
          size="small"
          startIcon={<Gesture />}
          onClick={(event) => {
            stopAccordionToggle(event);
            onSign(section.parcial);
          }}
          onMouseDown={stopAccordionToggle}>
          {i18n('entities.firma.parcial.signButton')}
        </Button>
      )}
      {onExportPdf && section.tasks.length > 0 && (
        <Button
          variant="outlined"
          size="small"
          startIcon={exportingPdf ? <CircularProgress size={16} /> : <PictureAsPdf />}
          disabled={exportingPdf}
          onClick={(event) => {
            stopAccordionToggle(event);
            onExportPdf();
          }}
          onMouseDown={stopAccordionToggle}>
          {i18n('entities.firma.parcial.exportPartialPdf')}
        </Button>
      )}
    </Box>
  );
}

export function ParcialSectionAccordion({
  section,
  canSignParcial,
  onSign,
  onExportPdf,
  exportingPdf,
  defaultExpanded = false,
  children,
}: {
  section: TaskSection;
  canSignParcial: (parcial: any) => boolean;
  onSign: (parcial: any) => void;
  onExportPdf?: () => void;
  exportingPdf?: boolean;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Accordion defaultExpanded={defaultExpanded} variant="outlined" sx={{mb: 1, '&:before': {display: 'none'}}}>
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          '& .MuiAccordionSummary-content': {
            my: 1,
            mr: 1,
            overflow: 'hidden',
          },
        }}>
        <ParcialSectionHeader
          section={section}
          canSignParcial={canSignParcial}
          onSign={onSign}
          onExportPdf={onExportPdf}
          exportingPdf={exportingPdf}
        />
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}

export function AnswerTablesView({tasks, loading, onOpen}: any) {
  const currentTenant = useSelector(authSelectors.selectCurrentTenant);
  const groups = useMemo(() => buildParcialGroups(tasks || []), [tasks]);
  const executorSignatureColumnLabel = getExecutorSignatureColumnLabel(currentTenant?.id);

  const buildCellStyles = (isWide: boolean) => {
    if (!isWide) {
      return {
        headerCellSx: {
          fontWeight: 700,
          borderLeft: '1px solid',
          borderRight: '1px solid',
          borderColor: 'divider',
        },
        bodyCellSx: {
          borderLeft: '1px solid',
          borderRight: '1px solid',
          borderColor: 'divider',
        },
      };
    }
    const L = CHILD_TASKS_WIDE_TABLE_LAYOUT;
    const cellPadding = {px: L.cellPaddingX, py: L.cellPaddingY};
    return {
      headerCellSx: {
        fontWeight: 700,
        fontSize: L.headerFontSize,
        letterSpacing: L.headerLetterSpacing,
        lineHeight: L.headerLineHeight,
        verticalAlign: 'middle' as const,
        borderLeft: '1px solid',
        borderRight: '1px solid',
        borderColor: 'divider',
        backgroundColor: (theme: any) => theme.palette.action.hover,
        ...cellPadding,
      },
      bodyCellSx: {
        fontSize: L.bodyFontSize,
        borderLeft: '1px solid',
        borderRight: '1px solid',
        borderColor: 'divider',
        ...cellPadding,
      },
    };
  };

  if (loading) return <Spinner />;

  if (!tasks?.length) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="textSecondary" textAlign="center">
            {i18n('entities.tarea.childTasksEmpty')}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {groups.map((group, groupIndex) => {
        const includeSignature = group.inspecciones.some((task) => getSignatureSource(getExecutorSignature(task)));
        const isWide = isChildTasksWideTableMode(currentTenant?.id, group.columns.length);
        const {headerCellSx, bodyCellSx} = buildCellStyles(isWide);
        const showSubsectionHeaders = isWide && hasSubsectionHeaderSpans(group.columns);
        const subsectionSpans = showSubsectionHeaders ? buildSubsectionHeaderSpans(group.columns) : [];
        const renderHeaderText = (text: string) => (isWide ? <TableHeaderLabel text={text} /> : text);

        return (
          <Card key={getRecordId(group.modelo) || groupIndex} sx={{mb: 2}} variant="outlined">
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={1}>
                <Typography variant="h6">{group.modelo?.titulo || i18n('entities.tarea.answerTableUntypedGroup')}</Typography>
                <Chip size="small" label={group.inspecciones.length} />
              </Box>
              {group.columns.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  {i18n('entities.tarea.answerTableNoQuestions')}
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{maxHeight: 520, overflowX: isWide ? 'auto' : undefined}}>
                  <Table
                    size="small"
                    stickyHeader
                    sx={isWide ? {tableLayout: 'auto', minWidth: 'max-content'} : undefined}>
                    <TableHead>
                      {showSubsectionHeaders ? (
                        <>
                          <TableRow>
                            <TableCell rowSpan={2} sx={{...headerCellSx, verticalAlign: 'middle'}}>
                              {i18n('entities.firma.parcial.number')}
                            </TableCell>
                            {subsectionSpans.map((span, spanIndex) => (
                              <TableCell
                                key={`subsection-${span.label}-${spanIndex}`}
                                colSpan={span.colSpan}
                                align="center"
                                sx={{...headerCellSx, borderBottomWidth: 1}}>
                                {renderHeaderText(span.label)}
                              </TableCell>
                            ))}
                            {includeSignature && (
                              <TableCell rowSpan={2} sx={{...headerCellSx, verticalAlign: 'middle'}}>
                                {executorSignatureColumnLabel}
                              </TableCell>
                            )}
                          </TableRow>
                          <TableRow>
                            {group.columns.map((column, index) => (
                              <TableCell
                                key={`${column.subsectionLabel || ''}-${column.label}-${index}`}
                                align="center"
                                sx={headerCellSx}>
                                {renderHeaderText(column.label)}
                              </TableCell>
                            ))}
                          </TableRow>
                        </>
                      ) : (
                        <TableRow>
                          <TableCell sx={headerCellSx}>{i18n('entities.firma.parcial.number')}</TableCell>
                          {group.columns.map((column, index) => (
                            <TableCell key={`${column.label}-${index}`} sx={headerCellSx} align={isWide ? 'center' : undefined}>
                              {renderHeaderText(column.label)}
                            </TableCell>
                          ))}
                          {includeSignature && (
                            <TableCell sx={headerCellSx}>{executorSignatureColumnLabel}</TableCell>
                          )}
                        </TableRow>
                      )}
                    </TableHead>
                    <TableBody>
                      {group.inspecciones.map((task, index) => (
                        <TableRow key={getRecordId(task)} hover sx={{cursor: 'pointer'}} onClick={() => onOpen?.(task)}>
                          <TableCell sx={bodyCellSx}>{getChildTableRowLabel(task, index, currentTenant?.id)}</TableCell>
                          {group.columns.map((column, colIndex) => (
                            <TableCell
                              key={`${column.subsectionLabel || ''}-${column.label}-${colIndex}`}
                              align={isWide ? 'center' : undefined}
                              sx={{
                                ...bodyCellSx,
                                whiteSpace: isWide ? 'nowrap' : 'pre-wrap',
                                ...(isParcialAnswerAlert(task, column, currentTenant?.id) ? PARCIAL_ANSWER_ALERT_CELL_SX : {}),
                              }}>
                              {getParcialAnswerText(task, column, '-', {tenantId: currentTenant?.id})}
                            </TableCell>
                          ))}
                          {includeSignature && (
                            <TableCell sx={bodyCellSx}>
                              <ExecutorSignatureCell task={task} />
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        );
      })}
    </>
  );
}

export function RequestVerificationDialog({open, onClose, onRequest, requesting, eligibleCount, excludeUserIds}: any) {
  const form = useForm({defaultValues: {validador: null}});
  const excluded = (excludeUserIds || []).map(String);

  const submit = (values) => {
    if (!values.validador) {
      Message.error(i18n('entities.firma.parcial.validatorRequired'));
      return;
    }
    const selectedId = getRecordId(values.validador);
    if (selectedId && excluded.includes(selectedId)) {
      Message.error(i18n('entities.firma.parcial.verifierIsExecutor'));
      return;
    }
    onRequest(values.validador);
  };

  return (
    <Dialog open={open} onClose={requesting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{i18n('entities.firma.parcial.requestButton')}</DialogTitle>
      <DialogContent sx={{pt: 1}}>
        <Typography variant="body2" color="textSecondary" mb={2}>
          {i18n('entities.firma.parcial.requestSummary', eligibleCount)}
        </Typography>
        <FormProvider {...form}>
          <form>
            <UserAutocompleteFormItem
              name="validador"
              label={i18n('entities.user.list.validators')}
              required={true}
              showCreate={false}
              autoFocus={true}
              filter={{canSign: true}}
              excludeIds={excluded}
              menuPortalTarget={document.body}
            />
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={requesting}>
          {i18n('common.cancel')}
        </Button>
        <Button
          onClick={form.handleSubmit(submit)}
          disabled={requesting}
          variant="contained"
          startIcon={requesting ? undefined : <Gesture />}>
          {requesting ? <CircularProgress size={20} /> : i18n('entities.firma.parcial.requestButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
